import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import enlightenBlackLogo from "../../assets/enlighten_black.png";

import { supabase } from "../../data/supabase/client";
import { PRODUCT_NAME } from "../../utils/terminology";
import { useAuth } from "./AuthContext";

interface LoginFormData {
  email: string;
  password: string;
}

const Login = () => {
  const { session, authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginFormData>({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = location.state?.from || { pathname: "/" };
  const redirectTo = typeof from === "string" ? from : `${from.pathname}${from.search || ""}${from.hash || ""}`;

  useEffect(() => {
    if (session) {
      navigate(redirectTo, { replace: true });
    }
  }, [session, navigate, redirectTo]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Track login attempt
      if (typeof window !== "undefined" && (window as any).posthog) {
        (window as any).posthog.capture("login_attempt", {
          email_domain: formData.email.split("@")[1]
        });
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) {
        // Track login failure
        if (typeof window !== "undefined" && (window as any).posthog) {
          (window as any).posthog.capture("login_failed", {
            reason: error.message,
            email_domain: formData.email.split("@")[1]
          });
        }
        throw error;
      }

      // Login success is tracked in AuthContext when the session is established
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred during login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img
            src={enlightenBlackLogo}
            alt={`${PRODUCT_NAME} Logo`}
            style={{ height: "48px" }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
          Sign in to continue to Enlighten
        </Typography>

        {(error || authError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {authError || error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              autoComplete="username"
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={isLoading}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 2 }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
          </Stack>
        </form>

        <Box textAlign="center" mt={1}>
          <Typography variant="body2">
            <Button
              component="a"
              href="/reset-password"
              color="primary"
              sx={{ p: 0, textTransform: "none", fontSize: "0.875rem" }}
            >
              Reset Password
            </Button>
          </Typography>
        </Box>
        {/*
        <Divider sx={{ my: 2 }}>or continue with</Divider>

        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            disabled
            sx={{ py: 1 }}
          >
            Google
          </Button>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<MicrosoftIcon />}
            disabled
            sx={{ py: 1 }}
          >
            Microsoft
          </Button>
        </Stack> */}
      </Paper>
    </Box>
  );
};

export default Login;
